import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { getOrderStatus } from "./guestApi";
import { alreadyAlerted, fire as firePager } from "./readyAlert";

const POLL_MS = 5000;

/** The three states a ticket moves THROUGH. Terminal states are not steps. */
const FLOW = ["new", "preparing", "ready"];

/**
 * Every status the server can return, mapped to a label key. The `|| "queued"`
 * fallback this replaces was the bug: `picked_up` had no entry, so a collected
 * order fell back to the FIRST label and the tracker appeared to rewind to
 * "Sent to kitchen" seconds after saying "picked up". A status the client does
 * not know must never silently render as the beginning.
 */
const LABEL = {
  new: "queued",
  preparing: "preparing",
  ready: "ready",
  picked_up: "collected",
  cancelled: "cancelled",
};

/** Once here, the ticket is done: stop polling and stop showing progress. */
const TERMINAL = ["picked_up", "cancelled"];

/**
 * P4 3c — the guest watches their own ticket after "Send to kitchen".
 *
 * Before this, sending an order was the end of the conversation: the screen said
 * "sent" and then knew nothing, so a guest had no way to tell a slow kitchen
 * from a lost order. It polls rather than opening a socket — the kitchen and
 * cashier boards already poll, a café guest's session is minutes long, and a
 * socket per guest phone is a lot of machinery for a progress bar.
 *
 * Points are deliberately NOT shown here. The card owns the balance; two
 * surfaces reporting points is how they drift apart.
 *
 * THE PAGER. When the ticket flips to READY this fires a real alert — chime,
 * vibration, a notification if the tab is hidden — and repaints loudly. That is
 * what makes this page a replacement for the plastic pager pucks a café hands
 * out, rather than a progress bar nobody watches: a guest is not staring at
 * their phone waiting, they are talking to someone, and a silent state change
 * on a screen face-down on the table is not an alert. See readyAlert.js.
 */
export default function OrderStatus({ orderId, initialOrder, guestName, cardHref, onOrderAnother }) {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.resolvedLanguage || i18n.language || "en").startsWith("ar");

  const [order, setOrder] = useState(initialOrder || null);
  const alive = useRef(true);
  // Whether THIS mount was the one that caught the transition — drives the loud
  // banner. Separate from the fired-once bookkeeping, which lives in
  // localStorage so a reload on an already-ready order re-paints without
  // re-ringing.
  const [justRang, setJustRang] = useState(false);

  const localName = useCallback((o) => (isAr && o?.name_ar ? o.name_ar : o?.name), [isAr]);

  const done = order ? TERMINAL.includes(order.status) : false;

  useEffect(() => {
    alive.current = true;
    if (!orderId || done) return undefined;   // nothing left to watch

    const tick = () =>
      getOrderStatus(orderId)
        .then((d) => { if (alive.current && d?.order) setOrder(d.order); })
        // A failed poll is not worth showing: the ticket is already placed, the
        // kitchen has it, and the next tick usually succeeds.
        .catch(() => {});

    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { alive.current = false; clearInterval(id); };
  }, [orderId, done]);

  /* ── the page: ring the moment this ticket becomes READY ────────────────── */
  //
  // Driven off the SAME status the tracker already renders — no second channel
  // to keep in sync, and nothing new for the kitchen to push. Keyed on the order
  // reaching `ready` rather than on a local previous-value comparison, because a
  // guest can arrive on this component with the order ALREADY ready (they closed
  // the tab and came back); the localStorage guard inside fire() is what makes
  // that repaint without re-ringing, and it is shared with the customer card so
  // two open surfaces cannot both buzz.
  useEffect(() => {
    if (!order || order.status !== "ready" || !orderId) return;
    const rang = firePager({
      orderId,
      title: t("scan_stay.order.ready_notif_title", { no: order.no }),
      body: order.table_number
        ? t("scan_stay.order.ready_table", { table: order.table_number })
        : t("scan_stay.order.ready_counter"),
    });
    // Show the banner both when we rang AND when this order has rung before:
    // a guest reopening a ready ticket should still land on the loud state.
    if (rang || alreadyAlerted(orderId)) setJustRang(true);
  }, [order, orderId, t]);

  if (!order) return null;

  // ONE source of truth for how we speak to this guest: does THIS ORDER have a
  // table? Never the venue's current service_type — those two can disagree, and
  // when they do the guest is told both things at once. An order placed while a
  // venue was in table mode keeps its table_number after the owner flips to
  // counter, and that order genuinely still belongs to table 2.
  //
  // Every service-flavoured string below derives from this single boolean, and
  // the STATUS HEADING is deliberately service-neutral ("Ready", not "Ready —
  // come and get it"): that slot renders for every order shape, so any pickup or
  // delivery wording baked into it is a claim it cannot support.
  const isTable = !!order.table_number;

  const stepIndex = FLOW.indexOf(order.status);
  const ready = order.status === "ready";
  const collected = order.status === "picked_up";
  const cancelled = order.status === "cancelled";

  const icon = cancelled ? "✕" : collected ? "🎉" : ready ? "🔔" : "✓";

  // Where-to-get-it line. When it's READY the instruction changes: a counter
  // guest has to walk up and collect, so telling them we'll bring it over is
  // simply wrong and leaves them sitting there.
  let whereLine;
  if (cancelled) {
    whereLine = null;
  } else if (ready) {
    whereLine = isTable
      ? t("scan_stay.order.ready_table", { table: order.table_number })
      : t("scan_stay.order.ready_counter");
  } else if (collected) {
    whereLine = null;
  } else {
    whereLine = isTable
      ? t("scan_stay.order.sent_table", { table: order.table_number })
      : t("scan_stay.order.sent_counter", { name: guestName || order.name || "" });
  }

  return (
    <div className={`ss-order-done${ready ? " is-ready" : ""}`}>
      {/* The loud state. A guest who has just been buzzed has to see WHY the
          moment they turn the phone over, from arm's length — hence a full
          banner rather than a changed word in the heading. */}
      {ready && justRang && (
        <div className="ss-paged" role="alert">
          <span className="ic" aria-hidden="true">🔔</span>
          <span className="tx">{t("scan_stay.order.ready_bang")}</span>
        </div>
      )}

      <div className={`ss-order-check${ready ? " ready" : ""}${collected ? " done" : ""}${cancelled ? " cancelled" : ""}`}>
        {icon}
      </div>
      <div className="ss-order-sent">{t(`card.status.${LABEL[order.status] || "queued"}`)}</div>
      <div className="ss-order-no mono">#{order.no}</div>

      {/* Progress only while there IS progress. A finished ticket showing a
          half-lit bar reads as still waiting. The caption that used to sit under
          the bar is gone: it repeated the heading verbatim, and two copies of the
          status is one more place for them to disagree. */}
      {!done && (
        <div className="cc-steps big" aria-hidden="true">
          {FLOW.map((s, i) => (
            <span key={s} className={`cc-step${stepIndex >= i ? " on" : ""}`} />
          ))}
        </div>
      )}

      <div className="ss-hint" style={{ marginTop: 6 }}>
        {order.qty} × {localName({ name: order.item_name, name_ar: order.item_name_ar })}
      </div>

      {whereLine && <div className="ss-hint">{whereLine}</div>}

      <p className="ss-paynote">
        {order.paid ? t("card.paid_done") : t("scan_stay.order.pay_note")}
      </p>

      <div className="cc-actions">
        {cardHref && <a className="ss-cta" href={cardHref}>{t("card.my_points")}</a>}
        <button type="button" className="ss-cta ghost" onClick={onOrderAnother}>
          {t("scan_stay.order_another")}
        </button>
      </div>
    </div>
  );
}
